/**
 * QPM Frontend SDK
 * E2E Performance Monitoring for React/JavaScript Applications
 */

import { TimeSync, TraceContext } from '../shared/timeSync.js';

// 전역 상태
let qpmInstance = null;

/**
 * QPM Client SDK
 */
class QPMClient {
    constructor(options = {}) {
        this.serviceName = options.serviceName || 'frontend';
        this.qpmServerUrl = options.qpmServerUrl || 'http://localhost:3005';
        this.userId = options.userId || 'anonymous';
        this.enabled = options.enabled !== false;
        this.sampleRate = options.sampleRate || 1.0;
        this.batchSize = options.batchSize || 10;
        this.flushInterval = options.flushInterval || 5000;
        this.slowThreshold = options.slowThreshold || 500; // ms

        // 내부 상태
        this.timeSync = new TimeSync({ qpmServerUrl: this.qpmServerUrl });
        this.pendingTraces = [];
        this.activeSpans = new Map();
        this.config = null;
        this.flushTimer = null;

        // 바인딩
        this._flush = this._flush.bind(this);
    }

    /**
     * SDK 초기화
     */
    async init() {
        if (!this.enabled) {
            return;
        }

        try {
            // 시간 동기화 (실패해도 계속 진행)
            try {
                await this.timeSync.start();
            } catch (syncError) {
                console.warn('[QPM] Time sync failed, using local time:', syncError.message);
            }

            // 설정 로드 (실패해도 계속 진행)
            try {
                await this._loadConfig();
            } catch (configError) {
                console.warn('[QPM] Config load failed, using defaults:', configError.message);
            }

            // 주기적 flush
            this.flushTimer = setInterval(this._flush, this.flushInterval);

            // 페이지 언로드 시 flush
            if (typeof window !== 'undefined') {
                window.addEventListener('beforeunload', () => this._flush(true));
            }

        } catch (error) {
            console.error('[QPM] Init failed:', error);
        }
    }

    /**
     * SDK 종료
     */
    destroy() {
        // 먼저 enabled를 false로 설정하여 추가 작업 방지
        this.enabled = false;

        // TimeSync 중지
        this.timeSync.stop();

        // flush 타이머 중지
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
            this.flushTimer = null;
        }

        // pending traces 비우기 (서버 전송 시도 없이)
        this.pendingTraces = [];
        this.activeSpans.clear();

    }

    /**
     * 설정 로드
     */
    async _loadConfig() {
        try {
            const response = await fetch(`${this.qpmServerUrl}/api/config/check`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    serviceName: this.serviceName,
                    userId: this.userId
                })
            });
            this.config = await response.json();

            // 설정 적용
            // 서버 응답 형식: { trace: boolean, reason: string, sampleRate?: number }
            if (this.config) {
                // 'enabled' 또는 'trace' 필드 확인 (서버 응답 형식에 따라)
                if (this.config.enabled !== undefined) {
                    this.enabled = this.config.enabled;
                } else if (this.config.trace !== undefined) {
                    this.enabled = this.config.trace;
                }
                this.sampleRate = this.config.sampleRate || this.sampleRate;
            }
        } catch (error) {
            console.warn('[QPM] Config load failed:', error);
        }
    }

    /**
     * 샘플링 결정
     */
    _shouldSample() {
        if (!this.enabled) return false;
        return Math.random() < this.sampleRate;
    }

    /**
     * 새 Trace 시작
     */
    startTrace(operationName, options = {}) {
        if (!this._shouldSample()) {
            return new NoopSpan();
        }

        const context = new TraceContext({
            syncedStartTime: this.timeSync.getSyncedTime(),
            offset: this.timeSync.getOffset()
        });

        const span = new Span({
            qpm: this,
            context,
            operationName,
            type: options.type || 'frontend',
            isCritical: options.critical || false,
            metadata: options.metadata || {}
        });

        this.activeSpans.set(context.spanId, span);
        return span;
    }

    /**
     * HTTP 요청 래핑 (fetch)
     */
    wrapFetch(originalFetch) {
        const qpm = this;

        return async function (url, options = {}) {
            const span = qpm.startTrace(`${options.method || 'GET'} ${url}`, {
                type: 'api',
                critical: true
            });

            // QPM 헤더 추가
            const headers = new Headers(options.headers || {});
            headers.set('x-qpm-trace', span.context?.toQPMHeader() || '');
            headers.set('traceparent', span.context?.toTraceparent() || '');

            try {
                const response = await originalFetch(url, { ...options, headers });

                span.setTag('http.status_code', response.status);
                if (!response.ok) {
                    span.setError({ message: `HTTP ${response.status}`, code: response.status });
                }

                span.end();
                return response;
            } catch (error) {
                span.setError(error);
                span.end();
                throw error;
            }
        };
    }

    /**
     * Trace 완료 및 큐에 추가
     */
    _completeTrace(span) {
        const trace = span.toTrace();
        trace.serviceName = this.serviceName;
        trace.userId = this.userId;

        // 상태 결정
        if (trace.error) {
            trace.status = 'error';
        } else if (trace.duration > this.slowThreshold) {
            trace.status = 'slow';
        } else {
            trace.status = 'success';
        }

        this.pendingTraces.push(trace);
        this.activeSpans.delete(span.context.spanId);

        // 배치 크기 도달 시 flush
        if (this.pendingTraces.length >= this.batchSize) {
            this._flush();
        }
    }

    /**
     * 서버로 전송
     */
    async _flush(sync = false) {
        if (this.pendingTraces.length === 0) return;

        const traces = [...this.pendingTraces];
        this.pendingTraces = [];

        const sendData = async () => {
            try {
                await fetch(`${this.qpmServerUrl}/api/traces`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(traces)
                });
            } catch (error) {
                console.warn('[QPM] Flush failed:', error);
                // 실패한 trace는 다시 큐에 추가
                this.pendingTraces.unshift(...traces);
            }
        };

        if (sync && navigator.sendBeacon) {
            // 페이지 언로드 시 sendBeacon 사용
            navigator.sendBeacon(
                `${this.qpmServerUrl}/api/traces`,
                JSON.stringify(traces)
            );
        } else {
            await sendData();
        }
    }
}

/**
 * Span 클래스
 */
class Span {
    constructor(options) {
        this.qpm = options.qpm;
        this.context = options.context;
        this.operationName = options.operationName;
        this.type = options.type;
        this.isCritical = options.isCritical;
        this.metadata = options.metadata;

        this.startTime = Date.now();
        this.syncedStartTime = options.context.syncedStartTime;
        this.endTime = null;
        this.duration = null;
        this.error = null;
        this.tags = {};
        this.children = [];
    }

    /**
     * 자식 span 생성
     */
    startChild(operationName, options = {}) {
        if (!this.context) return new NoopSpan();

        const childContext = this.context.createChild();
        const childSpan = new Span({
            qpm: this.qpm,
            context: childContext,
            operationName,
            type: options.type || this.type,
            isCritical: options.critical || false,
            metadata: options.metadata || {}
        });

        this.children.push(childSpan);
        this.qpm.activeSpans.set(childContext.spanId, childSpan);
        return childSpan;
    }

    /**
     * 태그 설정
     */
    setTag(key, value) {
        this.tags[key] = value;
        return this;
    }

    /**
     * 에러 설정
     */
    setError(error) {
        this.error = {
            message: error.message || String(error),
            code: error.code || 'ERROR',
            stack: error.stack
        };
        return this;
    }

    /**
     * Span 종료
     */
    end() {
        this.endTime = Date.now();
        this.duration = this.endTime - this.startTime;

        // 자식 span들도 종료
        this.children.forEach(child => {
            if (!child.endTime) child.end();
        });

        // 루트 span이면 trace 완료
        if (!this.context.parentSpanId) {
            this.qpm._completeTrace(this);
        }
    }

    /**
     * Trace 데이터 변환
     */
    toTrace() {
        const spans = this._collectSpans();
        const criticalPath = this._buildCriticalPath();

        return {
            serviceCallId: this.context.serviceCallId,
            operationName: this.operationName,
            startTime: this.startTime,
            syncedStartTime: this.syncedStartTime,
            duration: this.duration,
            error: this.error,
            criticalPath,
            spans,
            metadata: {
                ...this.metadata,
                ...this.tags
            }
        };
    }

    _collectSpans() {
        const spans = [{
            id: this.context.spanId,
            name: this.operationName,
            type: this.type,
            duration: this.duration,
            startOffset: 0,
            isCritical: this.isCritical
        }];

        this.children.forEach(child => {
            spans.push({
                id: child.context.spanId,
                parentId: this.context.spanId,
                name: child.operationName,
                type: child.type,
                duration: child.duration,
                startOffset: child.startTime - this.startTime,
                isCritical: child.isCritical
            });

            // 재귀적으로 손자 span 수집
            if (child.children.length > 0) {
                spans.push(...child._collectSpans().slice(1));
            }
        });

        return spans;
    }

    _buildCriticalPath() {
        const critical = [];

        if (this.isCritical) {
            critical.push({
                name: this.operationName,
                type: this.type,
                duration: this.duration
            });
        }

        this.children.forEach(child => {
            critical.push(...child._buildCriticalPath());
        });

        return critical;
    }
}

/**
 * Noop Span (샘플링 제외 시)
 */
class NoopSpan {
    startChild() { return new NoopSpan(); }
    setTag() { return this; }
    setError() { return this; }
    end() {}
}

// React Hook
export function useTrace(operationName, deps = []) {
    const spanRef = { current: null };

    // 컴포넌트 마운트 시 trace 시작
    if (typeof window !== 'undefined' && qpmInstance) {
        spanRef.current = qpmInstance.startTrace(operationName, {
            type: 'frontend',
            critical: true
        });
    }

    // cleanup 함수 반환
    return {
        span: spanRef.current,
        endTrace: () => {
            if (spanRef.current) {
                spanRef.current.end();
            }
        },
        startChild: (name, options) => {
            return spanRef.current?.startChild(name, options) || new NoopSpan();
        }
    };
}

// React Context Provider
export function createQPMProvider(React) {
    const QPMContext = React.createContext(null);

    function QPMProvider({ children, config }) {
        const [qpm, setQpm] = React.useState(null);

        React.useEffect(() => {
            const client = new QPMClient(config);
            client.init().then(() => {
                qpmInstance = client;
                setQpm(client);
            });

            return () => {
                client.destroy();
                qpmInstance = null;
            };
        }, []);

        return React.createElement(QPMContext.Provider, { value: qpm }, children);
    }

    function useQPM() {
        return React.useContext(QPMContext);
    }

    return { QPMProvider, useQPM, QPMContext };
}

// Singleton 접근
export function getQPM() {
    return qpmInstance;
}

// Factory
export function createQPM(options) {
    const client = new QPMClient(options);
    qpmInstance = client;
    return client;
}

export { QPMClient, Span, NoopSpan, TimeSync, TraceContext };
